import { createBaseFields, addressSchema, locationSchema } from './baseSchema.js';
import { commonUserFields, validateCommonFields, commonProfileFields, validateProfileFields, commonLocationFields, validateLocationFields, idDocumentSchema } from './userSchema.js';

/**
 * Professional collection schema
 */
export const professionalSchema = {
  ...createBaseFields('', ''),
  ...commonUserFields,
  ...commonProfileFields,
  ...commonLocationFields,
  idDocument: {
    storagePath: { _value: '', _rule: 'this.size() > 0', _immutable: true }
  },
  collegeIdDocument: {
    storagePath: { _value: '', _rule: 'this.size() > 0', _optional: true }
  },
  dateOfBirth: { _value: '', _rule: 'this.matches("^[0-9]{2}/[0-9]{2}/[0-9]{4}$")', _immutable: true },
  age: { _value: 0, _rule: 'this >= 17 && this <= 100', _immutable: true },
  sex: { _value: '', _enum: ['Male', 'Female'], _immutable: true },
  aadhaarLinkedMobile: { _value: '', _rule: 'this.matches("^[0-9]{10}$")', _immutable: true },
  employmentStatus: { _value: '', _enum: ['Student', 'Not employed', 'Employed but available on weekends', 'Currently looking for a job and available until placed'] },
  collegeName: { _value: '', _optional: true },
  courseJoiningMonth: { _value: '', _optional: true },
  courseJoiningYear: { _value: '', _optional: true },
  currentCourse: '',
  highestEducation: { _value: '', _enum: ['10th standard', '12th standard', 'Degree', 'Masters'] },
  hasWorkedBefore: false,
  skills: { _value: [], _rule: 'this.size() > 0' },
  skillSummary: { _value: '', _rule: 'this.size() > 0' },
  languages: [],

  resumeDocument: {
    storagePath: { _value: '', _rule: 'this.size() > 0', _optional: true }
  },
  certifications: { _value: [], _optional: true, _rule: 'this.size() <= 5' },

  isAvailableNow: { _value: false, _rule: 'oldData != null || this == false' },

  verificationStatus: { _value: 'pending', _enum: ['pending', 'verified', 'rejected'], _immutable: true, _optional: true, _rule: 'oldData != null || this == "pending"' },
  fcmToken: { _value: '', _optional: true },
  fcmTokenUpdatedAt: { _value: null, _rule: 'this is timestamp', _optional: true },
  notificationsEnabled: { _value: false, _optional: true },

  averageRating: { _value: 0.0, _optional: true, _rule: 'this >= 0 && this <= 5' },
  ratingCount: { _value: 0, _optional: true, _rule: 'this >= 0' },
  averageRating: { _value: 0.0, _optional: true, _rule: 'this >= 0 && this <= 5' },
  ratingCount: { _value: 0, _optional: true, _rule: 'this >= 0' },

  deactivatedByAdmin: { _value: false, _optional: true },
  adminDeactivationReason: { _value: '', _optional: true },
};

/**
 * Create a professional document
 * @param {string} uid - User ID
 * @param {string} displayName - Display name
 * @param {Object} address - Address object
 * @param {Object} location - Location object with lat, lng, accuracy
 * @param {string} dateOfBirth - Date of birth in DD/MM/YYYY format
 * @param {number} age - Age calculated from date of birth
 * @param {string} sex - Sex (Male/Female)
 * @param {string} aadhaarLinkedMobile - Aadhaar linked mobile number
 * @param {string} employmentStatus - Employment status
 * @param {string} collegeName - College name (mandatory for students)
 * @param {string} currentCourse - Current course (for students) or graduated course (for Degree/Masters)
 * @param {string} highestEducation - Highest educational qualification
 * @param {boolean} hasWorkedBefore - Work experience flag
 * @param {string[]} skills - Array of skills
 * @param {string} skillSummary - Summary of skills and experience
 * @param {string[]} languages - Array of languages
 * @param {boolean} isAvailableNow - Availability status
 * @returns {Object} Professional document
 */
export const createProfessionalDocument = ({
  uid,
  displayName,
  address,
  location,
  dateOfBirth,
  age,
  sex,
  aadhaarLinkedMobile,
  employmentStatus,
  collegeName = '',
  courseJoiningMonth = '',
  courseJoiningYear = '',
  currentCourse = '',
  highestEducation,
  hasWorkedBefore,
  skills,
  skillSummary,
  languages,

  isAvailableNow,
  phoneNumber,
  idStoragePath,
  collegeIdStoragePath,
  invitedBy,
  inviteCodeUsed,
  profilePicture,
  email,
  resumeStoragePath,
  certifications,
}) => {
  const baseFields = createBaseFields(uid, displayName);

  return {
    ...baseFields,
    address: {
      addressLine: address.addressLine || '',
      landmark: address.landmark || '',
      city: address.city || '',
      state: address.state || '',
      pincode: address.pincode || '',
      country: address.country || 'IN',
    },
    location: {
      lat: location.lat,
      lng: location.lng,
      addressFormatted: location.addressFormatted || '',
      ...(location.h3Index ? { h3Index: location.h3Index } : {}),
    },
    dateOfBirth: dateOfBirth || '',
    age: age || 0,
    sex: sex || '',
    aadhaarLinkedMobile: aadhaarLinkedMobile || '',
    employmentStatus: employmentStatus || '',
    collegeName: collegeName || '',
    courseJoiningMonth: courseJoiningMonth || '',
    courseJoiningYear: courseJoiningYear || '',
    currentCourse: currentCourse || '',
    highestEducation: highestEducation || '',
    hasWorkedBefore: Boolean(hasWorkedBefore),
    skills: Array.isArray(skills) ? skills : [],
    skillSummary: skillSummary || '',
    languages: Array.isArray(languages) ? languages : [],
    isAvailableNow: Boolean(isAvailableNow),
    phoneNumber: phoneNumber || '',
    email: email || '',
    idDocument: {
      storagePath: idStoragePath || '',
    },
    collegeIdDocument: collegeIdStoragePath ? {
      storagePath: collegeIdStoragePath,
    } : {},
    invitedBy: invitedBy || null,
    inviteCodeUsed: inviteCodeUsed || null,
    profilePicture: (profilePicture && profilePicture.storagePath && profilePicture.url) ? {
      storagePath: profilePicture.storagePath,
      url: profilePicture.url,
    } : {},
    resumeDocument: resumeStoragePath ? {
      storagePath: resumeStoragePath,
    } : {},
    certifications: Array.isArray(certifications) ? certifications : [],
  };
};

/**
 * Validate professional document
 * @param {Object} data - Professional data to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateProfessionalDocument = (data) => {
  const errors = [...validateCommonFields(data), ...validateProfileFields(data), ...validateLocationFields(data)];
  if (!data.idDocument?.storagePath) errors.push('idDocument.storagePath is required');
  if (data.employmentStatus === 'Student') {
    if (!data.collegeIdDocument?.storagePath) {
      errors.push('collegeIdDocument.storagePath is required for students');
    }
  }
  if (!data.dateOfBirth) errors.push('dateOfBirth is required');
  if (!data.age || data.age < 17 || data.age > 100) errors.push('valid age is required');
  if (!data.sex) errors.push('sex is required');
  if (!data.aadhaarLinkedMobile || data.aadhaarLinkedMobile.length !== 10) errors.push('valid aadhaarLinkedMobile is required');
  if (!data.employmentStatus) errors.push('employmentStatus is required');
  if (data.employmentStatus === 'Student' && !data.collegeName) errors.push('collegeName is required for students');
  if ((data.employmentStatus === 'Student' || data.highestEducation === 'Degree' || data.highestEducation === 'Masters') && !data.currentCourse) errors.push('currentCourse is required for students or when education is Degree/Masters');
  if (!data.highestEducation) errors.push('highestEducation is required');
  if (typeof data.hasWorkedBefore !== 'boolean') errors.push('hasWorkedBefore is required');
  if (!data.skills || data.skills.length === 0) errors.push('at least one skill is required');
  if (!data.skillSummary) errors.push('skillSummary is required');
  if (data.certifications && data.certifications.length > 5) errors.push('maximum 5 certifications allowed');

  return {
    isValid: errors.length === 0,
    errors,
  };
};
